import { Card } from "@/components/ui/card";
import { Text } from "@/components/ui/text";
import { Heading } from "@/components/ui/heading";
import {
  CreateFeedbackDTORate,
  OrgTransactionResponseDTODataItem,
  TransactionResponseDTODataItem,
  TransactionResponseDTODataItemMetadataFeedback,
} from "@/generated/api/models";
import { useTransactionInfo } from "@/utils/useTransactionInfo";
import {
  Avatar,
  AvatarFallbackText,
  AvatarImage,
} from "@/components/ui/avatar";
import {
  ChevronDownIcon,
  ChevronUpIcon,
  CloseIcon,
  Icon,
} from "@/components/ui/icon";
import { HStack } from "@/components/ui/hstack";
import { VStack } from "@/components/ui/vstack";
import { ComponentPropsWithoutRef, useMemo, useState } from "react";
import { LucideIcon, Users } from "lucide-react-native";
import { Badge, BadgeText } from "@/components/ui/badge";
import { Button, ButtonIcon, ButtonText } from "@/components/ui/button";
import {
  Modal,
  ModalBackdrop,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "@/components/ui/modal";
import { Divider } from "@/components/ui/divider";
import {
  Accordion,
  AccordionContent,
  AccordionContentText,
  AccordionHeader,
  AccordionIcon,
  AccordionItem,
  AccordionTitleText,
  AccordionTrigger,
} from "@/components/ui/accordion";

export function TransactionItemMini({
  transaction,
}: {
  transaction:
    | TransactionResponseDTODataItem
    | OrgTransactionResponseDTODataItem;
}) {
  const { getDetails, formatCents, dateFormatter } =
    useTransactionInfo(transaction);
  const details = useMemo(() => getDetails(transaction), [transaction]);

  return (
    <Card
      size="md"
      variant="filled"
      className="flex-row justify-between w-full items-center"
    >
      <HStack className="gap-4 items-center flex-1">
        <Avatar size="xs">
          <Icon as={details.icon} size="xs" className="stroke-white" />
        </Avatar>
        <VStack className="gap-0 flex-1">
          <Heading
            size="sm"
            className="text-ellipsis overflow-hidden whitespace-nowrap"
            numberOfLines={1}
            ellipsizeMode="tail"
          >
            {details.name}
          </Heading>
          <Text size="xs">
            {dateFormatter.format(new Date(transaction.date as string))}
          </Text>
        </VStack>
      </HStack>

      <Text size="sm" className="content-center flex-shrink-0">
        {formatCents(details.amount)}
      </Text>
    </Card>
  );
}

export function TransactionItemPooledAmount({
  pooled,
  amount,
  transaction,
}: {
  pooled?: {
    amount: string;
    shared: string;
  };
  amount: string;
  transaction:
    | OrgTransactionResponseDTODataItem
    | TransactionResponseDTODataItem;
}) {
  const [modalOpened, setModalOpened] = useState(false);

  if (!pooled) {
    return (
      <Text size="sm" className="content-center">
        {amount}
      </Text>
    );
  }

  return (
    <>
      <Button variant="link" size="xs" onPress={() => setModalOpened(true)}>
        <ButtonIcon size="xs" as={Users} />
        <ButtonText size="sm" className="content-center">
          {amount}
        </ButtonText>
      </Button>
      <TransactionItemPoolExplanationModal
        isOpen={modalOpened}
        onClose={() => setModalOpened(false)}
        amount={amount}
        pooled={pooled}
        transaction={transaction}
      />
    </>
  );
}

export function TransactionItemPoolExplanationModal({
  onClose,
  isOpen,
  amount,
  pooled,
}: Pick<ComponentPropsWithoutRef<typeof Modal>, "onClose" | "isOpen"> & {
  amount: string;
  pooled: {
    amount: string;
    shared: string;
  };
  transaction:
    | OrgTransactionResponseDTODataItem
    | TransactionResponseDTODataItem;
}) {
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalBackdrop />
      <ModalContent>
        <ModalHeader>
          <Heading size="md" className="text-typography-950">
            Team sharing scheme
          </Heading>
          <ModalCloseButton>
            <Icon
              as={CloseIcon}
              size="md"
              className="stroke-background-400 group-[:hover]/modal-close-button:stroke-background-700 group-[:active]/modal-close-button:stroke-background-900 group-[:focus-visible]/modal-close-button:stroke-background-900"
            />
          </ModalCloseButton>
        </ModalHeader>
        <ModalBody>
          <VStack className="gap-1">
            <Text size="sm">
              Team sharing scheme has been applied to this transaction.
            </Text>
            <HStack className="gap-2">
              <Text size="sm" className="font-semibold">
                + {pooled.amount}
              </Text>
              <Text size="sm">has been tipped</Text>
            </HStack>
            <HStack className="gap-2">
              <Text size="sm" className="font-semibold">
                − {pooled.shared}
              </Text>
              <Text size="sm">has been shared with teammates</Text>
            </HStack>
            <Divider />
            <HStack className="gap-2">
              <Text size="sm" className="font-semibold">
                = {amount}
              </Text>
              <Text size="sm">is a final calculated tip amount</Text>
            </HStack>
            <Accordion
              size="sm"
              variant="filled"
              type="single"
              isCollapsible={true}
              isDisabled={false}
              className="mt-5 w-full border border-outline-200"
            >
              <AccordionItem value="a">
                <AccordionHeader>
                  <AccordionTrigger>
                    {({ isExpanded }) => {
                      return (
                        <>
                          <AccordionTitleText>
                            What is team sharing scheme?
                          </AccordionTitleText>
                          {isExpanded ? (
                            <AccordionIcon
                              as={ChevronUpIcon}
                              className="ml-3"
                            />
                          ) : (
                            <AccordionIcon
                              as={ChevronDownIcon}
                              className="ml-3"
                            />
                          )}
                        </>
                      );
                    }}
                  </AccordionTrigger>
                </AccordionHeader>
                <AccordionContent>
                  <AccordionContentText size="sm">
                    Team sharing scheme is a setting, activated by business,
                    which helps to automatically distribute tips across the
                    team. If you feel distribution is wrong or unfair, please
                    feel free to contact team manager or us by support
                    chat.
                  </AccordionContentText>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button onPress={onClose}>
            <ButtonText>Okay</ButtonText>
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export function TransactionItemRate({
  rate,
}: {
  rate: CreateFeedbackDTORate;
}) {
  let action: ComponentPropsWithoutRef<typeof Badge>["action"] = "info";
  let rateString = "Good";

  switch (rate) {
    case "BAD":
      action = "error";
      rateString = "Bad 👎";
      break;
    case "GOOD":
      action = "info";
      rateString = "Good 👍";
      break;
    case "EXCELLENT":
      action = "success";
      rateString = "Excellent 🔥";
      break;
  }

  return (
    <Badge size="md" variant="solid" action={action}>
      <BadgeText>{rateString}</BadgeText>
    </Badge>
  );
}

export function TransactionItemFeedback(props: {
  content?: string;
  rate: CreateFeedbackDTORate;
}) {
  const { content, rate } = props;

  return (
    <VStack className="rounded-b-lg rounded-tr-lg border-background-300 border p-2 gap-1">
      <HStack>
        <TransactionItemRate rate={rate} />
      </HStack>
      <Text
        size="sm"
        className={content ? "text-typography-black" : "text-typography-500"}
      >
        {content || "(No written feedback)"}
      </Text>
    </VStack>
  );
}

export type TransactionBalanceChangeItemProps = {
  id: any;
  name: string;
  icon: LucideIcon | { fallbackName: string; src?: string };
  subtitle?: string;
  amount: number;
  feedback?: TransactionResponseDTODataItemMetadataFeedback;
  pooled: number | undefined;
  formatCents: (cents: number) => string;
  transaction:
    | TransactionResponseDTODataItem
    | OrgTransactionResponseDTODataItem;
};

export function TransactionBalanceChangeItem({
  icon,
  name,
  subtitle,
  amount,
  feedback,
  formatCents,
  pooled,
  transaction,
}: TransactionBalanceChangeItemProps) {
  return (
    <Card size="md" variant="filled" className="w-full">
      <VStack className="w-full gap-4">
        <HStack className="justify-between w-full">
          <HStack className="gap-4 items-center flex-1">
            <Avatar size="xs">
              {"fallbackName" in icon ? (
                <>
                  <AvatarFallbackText>{icon.fallbackName}</AvatarFallbackText>
                  <AvatarImage
                    source={{
                      uri: icon.src,
                    }}
                  />
                </>
              ) : (
                <Icon as={icon} size="xs" className="stroke-white" />
              )}
            </Avatar>
            <VStack className="gap-0 flex-1">
              <Heading
                size="sm"
                className="text-ellipsis overflow-hidden whitespace-nowrap"
                numberOfLines={1}
                ellipsizeMode="tail"
              >
                {name}
              </Heading>
              {subtitle && <Text size="xs">{subtitle}</Text>}
            </VStack>
          </HStack>

          <TransactionItemPooledAmount
            amount={formatCents(amount)}
            pooled={
              !!pooled && pooled !== amount
                ? {
                    amount: formatCents(pooled),
                    shared: formatCents(
                      pooled -
                        ("holdAmount" in transaction
                          ? transaction.holdAmount || transaction.amount
                          : transaction.amount)
                    ),
                  }
                : undefined
            }
            transaction={transaction}
          />
        </HStack>
        {feedback && (
          <TransactionItemFeedback
            rate={feedback.rate}
            content={feedback.comment || undefined}
          />
        )}
      </VStack>
    </Card>
  );
}
